<template>
    <label
        :for="inputId"
        class="relative block"
        :aria-label="$t('search.input.label')"
    >
        <ais-state-results>
            <template #default="{ status }">
                <span class="absolute inset-y-0 left-0 flex items-center pl-3 pb-px text-primary z-10">
                    <span v-if="status === 'stalled'" class="loading" />
                    <MiscIcon v-else name="search" />
                </span>
            </template>
        </ais-state-results>

        <input
            :id="inputId"
            ref="searchInput"
            type="text"
            :value="currentRefinement"
            class="input block w-full px-10"
            :placeholder="$t('search.input.placeholder')"
            @input="onExecute(($event.target as HTMLInputElement).value)"
            @focus="onShowResults()"
            @keydown.esc="onEscape()"
            @keydown.enter="onSubmit(currentRefinement)"
        >
        <button
            v-if="currentRefinement"
            type="button"
            class="btn btn-sm btn-ghost btn-circle absolute inset-y-0 right-0 m-auto mr-1 pb-px flex justify-center items-center text-primary"
            :aria-label="$t('search.input.reset')"
            @click="onReset()"
        >
            <MiscIcon name="x" />
        </button>
    </label>
</template>

<script setup lang="ts">
import { AisStateResults } from 'vue-instantsearch/vue3/es'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = defineProps<{
    currentRefinement: string
    showResults: boolean
}>()

const emit = defineEmits<{
    onExecute: [value: string]
    onSubmit: [value: string]
    onReset: [value: boolean]
    onShowResults: [value: boolean]
}>()

const searchInput = ref<HTMLInputElement>()

const uuid = useId()
const inputId = computed(() => uuid + 'autocompleteSearchInput')

const focus = () => {
    const el = searchInput.value
    if (!el) return
    el.focus()
    const end = el.value.length
    el.setSelectionRange(end, end) // caret to end so typing continues seamlessly after hand-off
}

defineExpose({ focus })

const onExecute = (value: string) => { emit('onExecute', value) }

const onShowResults = () => { emit('onShowResults', true) }

const onReset = () => { emit('onReset', true) }

const onSubmit = (value: string) => { emit('onSubmit', value) }

const onEscape = () => {
    // clear content and remove focus to compress search
    onReset()
    searchInput.value?.blur()
}
</script>
